import jsPDF from 'jspdf';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import { Sentry } from '@/lib/sentry';
import {
  documentTypeLabels,
  PdfTemplateName,
  PDF_TEMPLATE_NAMES,
  buildPixQrDataUrl,
} from './pdfTemplates/shared';
import {
  assertThermalSupported,
  isThermalFormat,
  thermalSpec,
  type PaperFormat,
} from './thermalPaper';
import { render as renderTermico } from './pdfTemplates/termico';
import { render as renderClassico } from './pdfTemplates/classico';
import { render as renderMinimalista } from './pdfTemplates/minimalista';
import { render as renderCorporativo } from './pdfTemplates/corporativo';
import { render as renderModerno } from './pdfTemplates/moderno';
import { render as renderElegante } from './pdfTemplates/elegante';
import { render as renderCompacto } from './pdfTemplates/compacto';
import { render as renderBotanico } from './pdfTemplates/botanico';
import { render as renderOnda } from './pdfTemplates/onda';
import { render as renderProposta } from './pdfTemplates/proposta';
import { render as renderTecnico } from './pdfTemplates/tecnico';
import { render as renderContrato } from './pdfTemplates/contrato';
import { render as renderRecibo } from './pdfTemplates/recibo';

// Re-export the public types for existing callers
export type {
  PDFQuoteItem,
  PDFClientInfo,
  PDFCompanyInfo,
  PDFGeneralPhoto,
  PDFQuoteData,
  PdfTemplateName,
} from './pdfTemplates/shared';
import type { PDFQuoteData } from './pdfTemplates/shared';

export { PDF_TEMPLATE_NAMES };

const templateRenderers: Record<
  PdfTemplateName,
  (doc: jsPDF, data: PDFQuoteData) => Promise<void>
> = {
  classico: renderClassico,
  minimalista: renderMinimalista,
  corporativo: renderCorporativo,
  moderno: renderModerno,
  elegante: renderElegante,
  compacto: renderCompacto,
  botanico: renderBotanico,
  onda: renderOnda,
  tecnico: renderTecnico,
  proposta: renderProposta,
};

/**
 * Resolve which template to use.
 * - Free users are ALWAYS forced to 'classico' (PRO gate).
 * - Unknown template values fall back to 'classico'.
 */
const resolveTemplate = (data: PDFQuoteData): PdfTemplateName => {
  if (!data.isPremium) return 'classico';
  const requested = data.template;
  if (requested && templateRenderers[requested]) return requested;
  return 'classico';
};

export interface PdfOutputOptions {
  /**
   * Formato do papel. `'a4'` (o padrão) mantém EXATAMENTE o caminho de sempre —
   * mesmo `new jsPDF()`, mesmos templates, mesmos bytes. Só quem pede bobina
   * entra no caminho térmico.
   */
  paper?: PaperFormat;
}

/** Altura da página-sonda da 1ª passada: folga absurda de propósito. */
const THERMAL_PROBE_HEIGHT_MM = 4000;
/** Avanço de papel depois do conteúdo, para o corte não comer a última linha. */
const THERMAL_TAIL_MM = 8;

/**
 * Gera o cupom em bobina.
 *
 * ─── POR QUE DUAS PASSADAS ───────────────────────────────────────────────────
 *
 * A bobina é contínua, mas o jsPDF exige a altura da página no construtor. As
 * duas alternativas eram:
 *
 *  (a) calcular a altura analiticamente, somando as mesmas medidas que o
 *      renderer usa — ou seja, uma SEGUNDA cópia do layout, que passa a
 *      divergir na primeira vez que alguém mexer só num dos lados. É a
 *      armadilha das cinco cópias da fórmula de total (#69), só que aqui a
 *      divergência corta conteúdo em silêncio;
 *  (b) rodar o MESMO renderer numa página gigante só para medir, e depois
 *      rodá-lo de novo na página do tamanho certo.
 *
 * (b) tem uma única fonte de verdade de layout, e só é barata porque o renderer
 * térmico é síncrono e não faz rede — por isso o QR do PIX é resolvido UMA vez,
 * aqui, antes das duas passadas (ele carrega um `txId` com timestamp: gerado
 * duas vezes, produziria QRs diferentes entre as passadas).
 */
async function generateThermalPDF(
  data: PDFQuoteData,
  paper: PaperFormat,
): Promise<jsPDF> {
  assertThermalSupported(data.documentType);
  if (!isThermalFormat(paper)) throw new Error(`Formato térmico inválido: ${paper}`);

  const spec = thermalSpec(paper);
  const renderOptions = { spec, pixQrDataUrl: await buildPixQrDataUrl(data) };

  const probe = new jsPDF({
    unit: 'mm',
    format: [spec.paperWidth, THERMAL_PROBE_HEIGHT_MM],
  });
  const measuredHeight = renderTermico(probe, data, renderOptions);

  const pageHeight = measuredHeight + THERMAL_TAIL_MM;
  const doc = new jsPDF({ unit: 'mm', format: [spec.paperWidth, pageHeight] });
  const finalHeight = renderTermico(doc, data, renderOptions);

  // O renderer não lê a altura da página (invariante documentada em
  // termico.ts), então as duas passadas convergem por construção. Se um dia
  // deixarem de convergir, o sintoma seria conteúdo cortado no fim do cupom
  // sem erro nenhum — então isto vira um evento no Sentry em vez de silêncio.
  if (finalHeight > pageHeight) {
    Sentry.captureMessage('Cupom térmico: 2ª passada excedeu a altura medida', {
      level: 'warning',
      extra: { measuredHeight, finalHeight, paper, documentType: data.documentType },
    });
  }

  return doc;
}

/**
 * Generates a PDF document for a quote using the selected template.
 * Unified entry point kept for API compatibility with existing callers.
 */
export async function generateQuotePDF(
  data: PDFQuoteData,
  options?: PdfOutputOptions,
): Promise<jsPDF> {
  const paper = options?.paper ?? 'a4';
  if (isThermalFormat(paper)) return generateThermalPDF(data, paper);

  const doc = new jsPDF();
  // Contrato tem layout próprio (corpo em texto + duas assinaturas + auditoria)
  // e ignora a seleção de template dos demais documentos.
  if (data.documentType === 'contrato') {
    await renderContrato(doc, data);
    return doc;
  }
  // Recibo tem layout próprio (bloco único com valor por extenso + assinatura do
  // emitente) e também ignora a seleção de template dos demais documentos.
  if (data.documentType === 'recibo') {
    await renderRecibo(doc, data);
    return doc;
  }
  const templateName = resolveTemplate(data);
  const renderer = templateRenderers[templateName];
  await renderer(doc, data);
  return doc;
}

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      const base64Data = base64String.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

/**
 * Entrega um jsPDF já montado ao sistema.
 *
 * ARMADILHA #95: no Capacitor o caminho web (`doc.save`, que faz `blob:` +
 * `<a download>`) é ignorado pelo WebView SEM erro, sem toast e sem arquivo. No
 * nativo é obrigatório gravar em Documentos pelo `Filesystem` e abrir com o
 * `FileOpener`. Isto vive numa função própria porque agora há DOIS produtores
 * de PDF (o documento único e a exportação de vários recibos) — e uma segunda
 * cópia da separação nativo/web voltaria a errar exatamente do mesmo jeito.
 */
export async function savePdfDocument(
  doc: jsPDF,
  filename: string,
  context?: Record<string, unknown>,
): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    doc.save(filename);
    return;
  }
  try {
    const pdfBlob = doc.output('blob');
    const base64Data = await blobToBase64(pdfBlob);
    const result = await Filesystem.writeFile({
      path: filename,
      data: base64Data,
      directory: Directory.Documents,
    });

    try {
      await FileOpener.open({ filePath: result.uri, contentType: 'application/pdf' });
    } catch (openError) {
      console.error('Erro ao abrir PDF:', openError);
      Sentry.captureException(openError, { extra: { context: 'FileOpener.open' } });
      alert(`PDF salvo com sucesso!\n\nLocal: Documentos/${filename}`);
    }
  } catch (error) {
    console.error('Erro ao salvar PDF no dispositivo:', error);
    Sentry.captureException(error, { extra: { context: 'Filesystem.writeFile', ...context } });
    alert(`Erro ao salvar PDF: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
  }
}

/**
 * Generates and downloads a PDF for a quote.
 * Uses Capacitor Filesystem for native Android/iOS, normal download for web.
 */
export async function downloadQuotePDF(
  data: PDFQuoteData,
  filename?: string,
  options?: PdfOutputOptions,
): Promise<void> {
  const doc = await generateQuotePDF(data, options);
  const documentLabel =
    documentTypeLabels[data.documentType]?.toLowerCase() || 'orcamento';
  const clientName = (data.client.name || 'cliente').replace(/[^a-zA-Z0-9]/g, '_');
  // O sufixo do formato deixa óbvio no gerenciador de arquivos qual arquivo é o
  // cupom e qual é o A4 — os dois saem do mesmo documento, no mesmo diretório.
  const paper = options?.paper ?? 'a4';
  const paperSuffix = isThermalFormat(paper) ? `-${thermalSpec(paper).paperWidth}mm` : '';
  const defaultFilename = `${documentLabel}-${clientName}${paperSuffix}-${Date.now()}.pdf`;

  await savePdfDocument(doc, filename || defaultFilename, {
    documentType: data.documentType,
  });
}
